import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { trackLeadConversion } from '@/components/TrackingScripts';
import { getQuestionSet } from '@/config/leadQuestionSets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cities = ['Genève', 'Lausanne', 'Montreux', 'Verbier', 'Crans-Montana', 'Villars', 'Fribourg', 'Neuchâtel', 'Valais'];

/**
 * ServiceLeadForm - Formulaire de capture de leads avec support mode "ultra"
 * @param {string} service - Service demandé (louer, vendre, renover, clients)
 * @param {string} pole - Pôle de service (nettoyage, conciergerie, renovation, marketing, immo)
 * @param {string} variant - Mode du formulaire: 'standard' ou 'ultra' (défaut: 'standard')
 * @param {Array} questions - DEPRECATED: Questions personnalisées (utilise maintenant leadQuestionSets)
 */
export const ServiceLeadForm = ({ service, pole, variant = 'standard', questions: deprecatedQuestions }) => {
  // Use new question sets system
  const questions = deprecatedQuestions && deprecatedQuestions.length > 0 
    ? deprecatedQuestions 
    : getQuestionSet(pole, variant);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    ...questions.reduce((acc, q) => ({ ...acc, [q.id]: '' }), {}),
    details: ''
  });
  const [errors, setErrors] = useState({});

  // Update formData when questions change (variant switch)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...questions.reduce((acc, q) => {
        // Keep existing value if present, otherwise empty
        acc[q.id] = prev[q.id] || '';
        return acc;
      }, {})
    }));
  }, [questions]);

  const validateField = (field, value, isRequired = false) => {
    const newErrors = { ...errors };
    
    switch(field) {
      case 'name':
        if (!value || value.trim().length < 2) {
          newErrors.name = 'Nom requis';
        } else {
          delete newErrors.name;
        }
        break;
      case 'email':
        if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Email valide requis';
        } else {
          delete newErrors.email;
        }
        break;
      case 'phone':
        if (!value || value.trim().length < 8) {
          newErrors.phone = 'Téléphone requis';
        } else {
          delete newErrors.phone;
        }
        break;
      case 'city':
        if (!value) {
          newErrors.city = 'Ville requise';
        } else {
          delete newErrors.city;
        }
        break;
      default:
        // Validate custom questions
        if (isRequired && (!value || value.toString().trim().length === 0)) {
          newErrors[field] = 'Champ requis';
        } else {
          delete newErrors[field];
        }
        break;
    }
    
    setErrors(newErrors);
  };

  const isFormValid = () => {
    // Base contact validation
    const baseValid = (
      formData.name.trim().length >= 2 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.phone.trim().length >= 8 &&
      formData.city
    );

    // Check required questions
    const questionsValid = questions.every(q => {
      if (q.required) {
        const value = formData[q.id];
        return value && value.toString().trim().length > 0;
      }
      return true;
    });

    return baseValid && questionsValid && Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value, isRequired = false) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (['name', 'email', 'phone', 'city'].includes(field) || isRequired) {
      validateField(field, value, isRequired);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const trackingData = {
        source: urlParams.get('utm_source') || 'direct',
        campaign: urlParams.get('utm_campaign') || null,
        page_origin: window.location.href,
        referrer: document.referrer || null
      };

      const answers = {};
      questions.forEach(q => {
        answers[q.id] = formData[q.id];
      });
      answers.details = formData.details;

      const leadData = {
        service: service,
        pole: pole,
        city: formData.city,
        answers: answers,
        contact: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        },
        ...trackingData
      };

      await axios.post(`${API}/leads`, leadData);
      
      // Track conversion event
      trackLeadConversion({
        service: leadData.service,
        pole: leadData.pole,
        city: leadData.city,
        source: leadData.source,
        campaign: leadData.campaign
      });
      
      toast.success('Demande envoyée avec succès !', {
        description: 'Nous vous contacterons sous 24h.',
        duration: 5000
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        ...questions.reduce((acc, q) => ({ ...acc, [q.id]: '' }), {}),
        details: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-lg sm:text-xl">Demander un devis</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Remplissez ce formulaire, nous vous contacterons sous 24h</CardDescription>
          </div>
          {variant === 'ultra' && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs self-start sm:self-auto">
              Formulaire qualifiant
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Dynamic questions */}
          {questions.map((q) => (
            <div key={q.id}>
              <Label htmlFor={q.id} className="text-sm">{q.label}</Label>
              {q.type === 'select' ? (
                <Select 
                  value={formData[q.id]} 
                  onValueChange={(val) => handleInputChange(q.id, val, q.required)}
                  required={q.required}
                >
                  <SelectTrigger className={`h-10 sm:h-11 text-sm ${errors[q.id] ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {q.options.map(opt => (
                      <SelectItem key={opt} value={opt} className="py-2 sm:py-3 text-sm">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={q.id}
                  value={formData[q.id]}
                  onChange={(e) => handleInputChange(q.id, e.target.value, q.required)}
                  placeholder={q.placeholder}
                  className={`h-10 sm:h-11 text-sm ${errors[q.id] ? 'border-red-500' : ''}`}
                  required={q.required}
                />
              )}
              {errors[q.id] && <p className="text-xs text-red-500 mt-1">{errors[q.id]}</p>}
            </div>
          ))}

          {/* City - Dropdown only for conciergerie, free text for others */}
          <div>
            <Label htmlFor="city" className="text-sm">Dans quelle ville se trouve le bien ? *</Label>
            {pole === 'conciergerie' ? (
              <Select value={formData.city} onValueChange={(val) => handleInputChange('city', val)} required>
                <SelectTrigger className={`h-10 sm:h-11 text-sm ${errors.city ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(city => (
                    <SelectItem key={city} value={city} className="py-2 sm:py-3 text-sm">{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Ex: Genève, Lausanne, Montreux..."
                className={`h-10 sm:h-11 text-sm ${errors.city ? 'border-red-500' : ''}`}
                required
              />
            )}
            {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
          </div>

          {/* Contact fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t">
            <div>
              <Label htmlFor="name" className="text-sm">Nom complet *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                className={`h-10 sm:h-11 text-sm ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label htmlFor="email" className="text-sm">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                className={`h-10 sm:h-11 text-sm ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
          </div>

          <div>
            <Label htmlFor="phone" className="text-sm">Téléphone *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              required
              className={`h-10 sm:h-11 text-sm ${errors.phone ? 'border-red-500' : ''}`}
            />
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <div>
            <Label htmlFor="details" className="text-sm">Détails supplémentaires (optionnel)</Label>
            <Textarea
              id="details"
              value={formData.details}
              onChange={(e) => handleInputChange('details', e.target.value)}
              rows={3}
              className="resize-none text-sm"
            />
          </div>

          <Button
            type="submit"
            className="btn-premium w-full h-11 sm:h-12 text-sm sm:text-base rounded-full"
            disabled={loading || !isFormValid()}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Envoi...' : 'Envoyer ma demande'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};